// A rough, deliberately uncalibrated read on how much a given injury matters to
// a point spread, and which way the two teams' injury pictures lean.
//
// It is not modelled against our own results and cannot be — there are barely a
// hundred scored games. It is a heuristic for *ranking* a team's injuries and
// giving the page a one-line "leans CIN" caption, nothing more. The same honesty
// that applies to edge.js applies here: the only real content is the position
// weights and the availability estimates, and both are stated, not fitted.
//
// impact(player) = positionWeight(pos) * (1 - playProbability(status))
//
// so a player who is definitely out at a high-value position scores near the
// position weight, and a questionable special-teamer scores near zero.

// Relative worth of a position to a point spread. QB is the anchor at 1.0; a
// missing edge rusher or a cornerback is worth roughly half a quarterback, an
// off-ball starter roughly a third, a running back or interior lineman less
// again, and kicking-game roles almost nothing. These are round numbers on
// purpose — the ranking only needs the tiers to be right.
export const POSITION_WEIGHT = {
  QB: 1.0,

  EDGE: 0.55,
  DE: 0.55,
  OLB: 0.55,
  CB: 0.55,
  LT: 0.55,
  WR: 0.5,

  DT: 0.42,
  DL: 0.42,
  S: 0.42,
  FS: 0.42,
  SS: 0.42,
  LB: 0.42,
  MLB: 0.42,
  ILB: 0.42,
  TE: 0.42,

  RB: 0.28,
  T: 0.28,
  OT: 0.28,
  RT: 0.28,
  G: 0.25,
  OG: 0.25,
  LG: 0.25,
  RG: 0.25,
  C: 0.25,
  OL: 0.25,
  FB: 0.22,

  K: 0.05,
  PK: 0.05,
  P: 0.05,
  LS: 0.04,
};

export const DEFAULT_POSITION_WEIGHT = 0.2;

// Chance the player actually suits up, by injury designation. "Out", IR, PUP and
// a suspension are effectively zero; "Doubtful" is close; "Questionable" is a
// coin flip that leans towards playing; anything else (a bare mention, "Probable")
// is treated as almost certain.
export const PLAY_PROBABILITY = [
  [/(injured reserve|\bi\.?r\.?\b|reserve\/)/, 0],
  [/(\bout\b|inactive)/, 0],
  [/(pup|physically unable)/, 0],
  [/(suspend|suspension)/, 0],
  [/doubtful/, 0.1],
  [/questionable/, 0.55],
  [/(probable|day.?to.?day|likely|limited)/, 0.9],
];

export const DEFAULT_PLAY_PROBABILITY = 0.9;

const EPSILON = 1e-6;

function normalizePosition(pos) {
  return String(pos || "").trim().toUpperCase();
}

export function positionWeight(pos) {
  const key = normalizePosition(pos);
  return Object.prototype.hasOwnProperty.call(POSITION_WEIGHT, key)
    ? POSITION_WEIGHT[key]
    : DEFAULT_POSITION_WEIGHT;
}

export function playProbability(status) {
  const text = String(status || "").trim().toLowerCase();
  if (!text) return DEFAULT_PLAY_PROBABILITY;
  for (const [pattern, probability] of PLAY_PROBABILITY) {
    if (pattern.test(text)) return probability;
  }
  return DEFAULT_PLAY_PROBABILITY;
}

// "high" | "mid" | "low" | "minimal" — used to tint the position badge so the
// eye lands on the quarterback before the long snapper.
export function weightTier(pos) {
  const weight = positionWeight(pos);
  if (weight >= 0.7) return "high";
  if (weight >= 0.38) return "mid";
  if (weight >= 0.15) return "low";
  return "minimal";
}

export function impact(player) {
  if (!player) return 0;
  return positionWeight(player.position) * (1 - playProbability(player.status));
}

// Returns a new array, each entry with an `impact` field, sorted most-impactful
// first. Does not mutate the input.
export function rankInjuries(players) {
  return (players || [])
    .map((player) => ({ ...player, impact: impact(player) }))
    .sort((a, b) => b.impact - a.impact);
}

export function teamImpact(players) {
  return (players || []).reduce((sum, player) => sum + impact(player), 0);
}

// Which team's injury picture is worse, and by how much. `lean` names the
// HEALTHIER team — the one an injury edge would favour — or null when the two
// are level. `homeShare` / `awayShare` are the split of total injury cost, so a
// bar can be drawn straight from them; they sum to 1 (0.5/0.5 when neither team
// has an injury).
export function directionalLean(homePlayers, awayPlayers, homeAbbr, awayAbbr) {
  const homeCost = teamImpact(homePlayers);
  const awayCost = teamImpact(awayPlayers);
  const total = homeCost + awayCost;

  const homeShare = total < EPSILON ? 0.5 : homeCost / total;
  const awayShare = total < EPSILON ? 0.5 : awayCost / total;
  const delta = homeCost - awayCost;

  let lean = null;
  if (Math.abs(delta) >= 0.05) {
    // The team with the LOWER injury cost is the one favoured.
    lean = delta > 0 ? awayAbbr : homeAbbr;
  }

  return { homeShare, awayShare, lean, delta };
}
